import React from 'react';
import { diffLines } from 'diff';
import { Box, Flex, Text } from 'core/components';
import styled from 'styled-components';

const DiffContainer = styled(Box)`
  white-space: pre-wrap;
  word-break: break-word;
`;

const LineContainer = styled(Flex)`
  align-items: center;
  max-width: 100%;
  width: 100%;
`;

const ContentWrapper = styled(Box)`
  flex: 1;
  display: flex;
  min-height: 0;
  width: 100%;
`;

const LineIndicator = styled(Box)`
  min-width: 24px;
  user-select: none;
  font-family: ${({ theme }) => theme.fonts.monospace};
  font-weight: 500;
  position: sticky;
  left: 0;
  padding-right: 8px;

  &.added {
    color: ${({ theme }) => (theme.name === 'dark' ? '#85a578' : '#28a745')};
  }

  &.removed {
    color: ${({ theme }) => (theme.name === 'dark' ? '#cd6a7f' : '#dc3545')};
  }
`;

const DiffPart = styled(Box)`
  margin: 0;
  white-space: pre-wrap;
  width: 100%;
  line-height: 1.7;

  &.added {
    background-color: ${({ theme }) => (theme.name === 'dark' ? 'rgba(40, 167, 69, 0.2)' : '#d4edda')};
    color: ${({ theme }) => (theme.name === 'dark' ? '#85a578' : '#155724')};
    border-left: 3px solid ${({ theme }) => (theme.name === 'dark' ? '#28a745' : '#28a745')};
  }

  &.removed {
    background-color: ${({ theme }) => (theme.name === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da')};
    color: ${({ theme }) => (theme.name === 'dark' ? '#cd6a7f' : '#721c24')};
    border-left: 3px solid ${({ theme }) => (theme.name === 'dark' ? '#dc3545' : '#dc3545')};
  }

  &.unchanged {
    background-color: transparent;
  }
`;

const getPartClassName = (part) => {
  if (part.added) {
    return 'added';
  }
  if (part.removed) {
    return 'removed';
  }
  return 'unchanged';
};

const getLineIndicator = (part) => {
  if (part.added) {
    return '+';
  }
  if (part.removed) {
    return '-';
  }
  return ' ';
};

let keyCounter = 0;

const generateUniqueKey = (part) => {
  keyCounter += 1;

  let typePrefix;
  switch (true) {
    case part.added:
      typePrefix = 'add';
      break;
    case part.removed:
      typePrefix = 'rem';
      break;
    default:
      typePrefix = 'unch';
  }

  return `diff-line-${typePrefix}-${keyCounter}`;
};

const DiffLine = ({ className, lineIndicator, children }) => (
  <LineContainer>
    <LineIndicator className={className}>{lineIndicator}</LineIndicator>
    <ContentWrapper>
      <DiffPart className={className}>
        <Text>{children}</Text>
      </DiffPart>
    </ContentWrapper>
  </LineContainer>
);

const TextDiffViewer = ({ oldText, newText }) => {
  const computedDiff = diffLines(oldText || '', newText || '');
  const result = [];

  computedDiff.forEach((part) => {
    if (!part.value) {
      return;
    }

    const className = getPartClassName(part);
    const lineIndicator = getLineIndicator(part);

    const lines = part.value.split('\n');

    lines.forEach((line, lineIndex) => {
      if (lineIndex === lines.length - 1 && line === '') {
        return;
      }

      result.push(
        <DiffLine key={generateUniqueKey(part)} className={className} lineIndicator={lineIndicator}>
          {line || '\u00A0'}
        </DiffLine>
      );
    });
  });

  return <DiffContainer>{result}</DiffContainer>;
};
export default TextDiffViewer;
